/**
 * Corbeille des fiches entreprise — administrateur de PLATEFORME (plan V4 §45).
 *
 * Une fiche supprimée depuis son écran arrive ici avec toutes ses données
 * liées. Pendant `retention_days` jours (30), elle se restaure à l'identique ;
 * passé ce délai, le serveur l'efface de lui-même. Deux gestes manuels :
 * supprimer définitivement une seule fiche, ou vider toute la corbeille.
 *
 * L'écran n'est pas une frontière de sécurité : un administrateur
 * d'organisation ordinaire reçoit 403 du serveur et voit un écran vide expliqué.
 */
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle, ArrowLeft, Clock, RotateCcw, ShieldCheck, Trash2 } from "lucide-react";
import api, { apiError } from "@/lib/crmApi";
import { PageHeader, Loader, Empty } from "@/crm/ui";
import Modal from "@/crm/components/Modal";

const dateFr = (iso) => (iso
  ? new Date(iso).toLocaleString("fr-FR", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })
  : "—");

const contenu = (item) => {
  const parts = Object.entries(item.counts_labelled || {}).map(([label, n]) => `${n} ${label}`);
  return parts.length ? parts.join(" · ") : "aucune donnée liée";
};

export default function Trash() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);           // { items, retention_days }
  const [denied, setDenied] = useState(false);
  const [busy, setBusy] = useState(null);           // id de la fiche en cours, ou "all"
  const [confirm, setConfirm] = useState(null);     // { item } ou { all: true }

  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get("/trash/companies");
      setData(d);
      setDenied(false);
    } catch (e) {
      setDenied(e.response?.status === 403);
      setData({ items: [], retention_days: 30 });
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const restaurer = async (item) => {
    setBusy(item.id);
    try {
      await api.post(`/trash/companies/${item.id}/restore`);
      toast.success(`« ${item.nom} » restaurée avec ses données`);
      await load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Restauration impossible"); }
    finally { setBusy(null); }
  };

  const supprimer = async (item) => {
    setBusy(item.id);
    try {
      await api.delete(`/trash/companies/${item.id}`);
      toast.success(`« ${item.nom} » supprimée définitivement`);
      setConfirm(null);
      await load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Suppression impossible"); }
    finally { setBusy(null); }
  };

  const vider = async () => {
    setBusy("all");
    try {
      const { data: r } = await api.delete("/trash/companies");
      toast.success(`Corbeille vidée : ${r.companies} fiche(s) supprimée(s) définitivement`);
      setConfirm(null);
      await load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Vidage impossible"); }
    finally { setBusy(null); }
  };

  if (data === null) return <Loader label="Chargement de la corbeille…" />;
  const items = data.items || [];
  const retention = data.retention_days || 30;

  return (
    <div>
      <PageHeader
        title="Corbeille"
        subtitle={`Fiches entreprise supprimées, restaurables pendant ${retention} jours avec toutes leurs données. Passé ce délai, elles sont effacées définitivement.`}
        actions={(
          <div className="flex flex-wrap gap-2">
            {/* Pas d'entrée de menu : on revient d'où l'on est venu. */}
            <button type="button" data-testid="trash-back" onClick={() => navigate("/entreprises")}
              className="inline-flex items-center gap-2 rounded-xl border border-slate-300 bg-white px-3.5 py-2 text-sm text-slate-700 hover:bg-slate-50">
              <ArrowLeft size={15} /> Entreprises
            </button>
            {!denied && items.length > 0 && (
              <button type="button" data-testid="trash-empty" onClick={() => setConfirm({ all: true })} disabled={busy !== null}
                className="inline-flex items-center gap-2 rounded-xl border border-rose-200 bg-white px-3.5 py-2 text-sm font-medium text-rose-600 hover:bg-rose-50 disabled:opacity-50">
                <Trash2 size={15} /> Vider la corbeille
              </button>
            )}
          </div>
        )}
      />

      {denied ? (
        <div className="bg-white rounded-xl border border-slate-200" data-testid="trash-denied">
          <Empty label="Réservé à l'administrateur de la plateforme (PLATFORM_ADMIN_EMAILS)" icon={ShieldCheck} />
        </div>
      ) : items.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200" data-testid="trash-empty-state">
          <Empty label="La corbeille est vide" icon={Trash2} />
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="trash-table">
              <thead>
                <tr className="text-left text-xs text-slate-500 border-b border-slate-200 bg-slate-50">
                  {["Entreprise", "SIRET", "Supprimée le", "Contenu", "Expire", ""].map((h) => (
                    <th key={h} className="px-4 py-3 font-medium">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {items.map((item) => {
                  const bientot = item.days_left <= 7;
                  return (
                    <tr key={item.id} className="border-b border-slate-50 hover:bg-slate-50" data-testid="trash-row">
                      <td className="px-4 py-3">
                        <div className="font-medium text-slate-800">{item.nom || "—"}</div>
                        <div className="text-xs text-slate-500">
                          {[item.code_postal, item.ville].filter(Boolean).join(" ") || "—"}
                          {item.status ? ` · ${item.status}` : ""}
                        </div>
                      </td>
                      <td className="px-4 py-3 font-mono text-xs text-slate-600">{item.siret || "—"}</td>
                      <td className="px-4 py-3 text-slate-600">
                        <div>{dateFr(item.deleted_at)}</div>
                        <div className="text-xs text-slate-400">par {item.deleted_by?.name || item.deleted_by?.email || "—"}</div>
                      </td>
                      <td className="px-4 py-3 text-xs text-slate-600">{contenu(item)}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${bientot ? "bg-amber-50 text-amber-700" : "bg-slate-100 text-slate-600"}`}>
                          <Clock size={12} />
                          {item.days_left === 0 ? "aujourd'hui" : `dans ${item.days_left} j`}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex justify-end gap-2">
                          <button type="button" data-testid="trash-restore" onClick={() => restaurer(item)} disabled={busy !== null}
                            className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50">
                            <RotateCcw size={13} /> Restaurer
                          </button>
                          <button type="button" data-testid="trash-delete" onClick={() => setConfirm({ item })} disabled={busy !== null}
                            className="inline-flex items-center gap-1.5 rounded-lg border border-rose-200 px-3 py-1.5 text-xs font-medium text-rose-600 hover:bg-rose-50 disabled:opacity-50">
                            <Trash2 size={13} /> Supprimer
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {confirm && (
        <Modal onClose={() => setConfirm(null)} testid="trash-confirm" label="Suppression définitive" className="max-w-[480px]">
          <div className="flex items-center gap-3 mb-3 text-rose-600">
            <div className="h-11 w-11 rounded-full bg-rose-50 flex items-center justify-center"><AlertTriangle size={22} /></div>
            <h3 className="text-base font-semibold text-slate-800">
              {confirm.all ? "Vider la corbeille ?" : `Supprimer définitivement « ${confirm.item.nom} » ?`}
            </h3>
          </div>
          <p className="text-sm text-slate-600">
            {confirm.all
              ? `${items.length} fiche(s) et toutes leurs données liées seront effacées. Cette action est irréversible.`
              : "La fiche et ses données liées (contacts, activités, notes, appels…) seront effacées. Cette action est irréversible."}
          </p>
          <div className="mt-5 flex justify-end gap-2">
            <button type="button" onClick={() => setConfirm(null)} disabled={busy !== null}
              className="rounded-lg border border-slate-300 px-3.5 py-2 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-50">
              Annuler
            </button>
            <button type="button" data-testid="trash-confirm-delete" disabled={busy !== null}
              onClick={() => (confirm.all ? vider() : supprimer(confirm.item))}
              className="inline-flex items-center gap-2 rounded-lg bg-rose-600 px-3.5 py-2 text-sm font-medium text-white hover:bg-rose-700 disabled:opacity-50">
              <Trash2 size={15} /> Supprimer définitivement
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}
